import { createSelector } from "reselect";
import fuzzysort from "fuzzysort";

import { CommandPaletteAction, CommandPaletteBucket } from "components/CommandPalette/types";

import { CommandPaletteStore } from "./types";

const selectBuckets = (state: CommandPaletteStore) => state.buckets;
const selectSearchQuery = (state: CommandPaletteStore) => state.searchQuery;
const selectCurrentView = (state: CommandPaletteStore) => state.currentView;
const selectSubListItems = (state: CommandPaletteStore) => state.subListItems;
const selectRawActiveActionId = (state: CommandPaletteStore) => state.activeActionId;

const selectBucketKeywordsMap = createSelector(
  [selectBuckets],
  (buckets: CommandPaletteBucket[]) =>
    new Map(buckets.map((bucket) => [bucket.id, bucket.keywords ?? []]))
);

const selectAllActions = createSelector([selectBuckets], (buckets: CommandPaletteBucket[]) => {
  return buckets.flatMap((bucket) => bucket.getActions());
});

const selectFilteredActions = createSelector(
  [selectAllActions, selectSearchQuery, selectBucketKeywordsMap],
  (actions, query, bucketKeywordsMap) => {
    if (!query) return actions;

    const alwaysVisible = actions.filter((a) => a.alwaysVisible);
    const filterable = actions.filter((a) => !a.alwaysVisible);

    const searchable = filterable.map((action) => {
      const bucketKeywords = bucketKeywordsMap.get(action.bucketId) ?? [];
      const allKeywords = [...(action.keywords ?? []), ...bucketKeywords];

      return {
        ...action,
        keywordsString: allKeywords.join(" "),
      };
    });

    const results = fuzzysort.go(query, searchable, {
      keys: ["title", "keywordsString"],
      threshold: -10000,
    });

    return [...results.map((r) => r.obj), ...alwaysVisible];
  }
);

export const selectGroupedByBucket = createSelector(
  [selectFilteredActions],
  (actions: CommandPaletteAction[]) => {
    const grouped = new Map<string, CommandPaletteAction[]>();

    actions.forEach((action) => {
      if (!grouped.has(action.bucketId)) {
        grouped.set(action.bucketId, []);
      }
      grouped.get(action.bucketId)!.push(action);
    });

    return grouped;
  }
);

export const selectNavigableActions = createSelector(
  [selectCurrentView, selectGroupedByBucket, selectBuckets, selectSubListItems],
  (view, groupedByBucket, buckets, subListItems) => {
    if (view === "sub-list") return subListItems;

    // Flatten grouped actions in bucket order (matching visual rendering)
    return buckets.flatMap((bucket) => groupedByBucket.get(bucket.id) || []);
  }
);

export const selectActiveActionId = createSelector(
  [selectRawActiveActionId, selectNavigableActions],
  (activeActionId, navigableActions) => {
    return activeActionId ?? navigableActions[0]?.id ?? null;
  }
);
